import React, { useState } from "react";
import { apiFetch } from "../../services/api";
import GoogleContinueButton from "./GoogleContinueButton";
import { continueWithGoogle, persistAuthSession } from "../auth/googleSso";

/**
 * Thin auth soft-gate ("See it live" / "Try the full loop").
 * Google one-click or email magic link; full signup stays available.
 * Signing in provisions the user's own trial workspace.
 */
export default function SoftGateSheet({
  open,
  onClose,
  onAuthenticated,
  onNeedsGoogleSignup,
  onFullSignup,
  title = "Try the full loop",
  subtitle = "Create a free workspace to run this on sample notes — or your own.",
  closeLabel = "Not now",
}) {
  const [email, setEmail] = useState("");
  const [terms, setTerms] = useState(false);
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [googleBusy, setGoogleBusy] = useState(false);
  const [error, setError] = useState("");

  if (!open) return null;

  const handleGoogle = async () => {
    setGoogleBusy(true);
    setError("");
    try {
      const result = await continueWithGoogle();
      if (result.ok) {
        persistAuthSession(result.data);
        onAuthenticated?.(result.data?.user);
        return;
      }
      if (result.needsSignupDetails) {
        onNeedsGoogleSignup?.({
          idToken: result.idToken,
          email: result.email,
          displayName: result.displayName,
        });
        return;
      }
      setError(result.message || "Google sign-in failed");
    } finally {
      setGoogleBusy(false);
    }
  };

  const handleMagicLink = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    setError("");
    try {
      const res = await apiFetch("/api/auth/magic-link/request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim(),
          terms_accepted: terms,
          gdpr_consent: terms,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(
          (typeof data.detail === "string" && data.detail) ||
            "Could not send the sign-in link. Please try again."
        );
        return;
      }
      setSent(true);
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" role="dialog" aria-modal="true" aria-label="Start free trial">
      <button type="button" className="absolute inset-0 bg-slate-900/50" aria-label="Close" onClick={onClose} />
      <div className="relative w-full sm:max-w-md max-h-[92vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl bg-white p-5 pb-8 shadow-xl">
        <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-slate-300 sm:hidden" aria-hidden />
        <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
        <p className="mt-1 text-sm text-slate-600">{subtitle}</p>

        {sent ? (
          <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-3 text-sm text-emerald-900">
            Check your email — open the sign-in link <strong>on this device</strong> to
            land in your workspace.
          </div>
        ) : (
          <>
            <form onSubmit={handleMagicLink} className="mt-4 space-y-3">
              <label className="block text-sm font-medium text-slate-800">
                Email
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm"
                  autoComplete="email"
                />
              </label>
              <label className="flex items-start gap-2 text-xs text-slate-600">
                <input
                  type="checkbox"
                  checked={terms}
                  onChange={(e) => setTerms(e.target.checked)}
                  className="mt-0.5"
                />
                <span>
                  For a new workspace, I accept the Terms and Privacy Policy.
                  (Existing accounts can sign in without re-accepting.)
                </span>
              </label>
              <button
                type="submit"
                disabled={busy}
                className="w-full rounded-lg bg-blue-700 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
              >
                {busy ? "Sending…" : "Email me a sign-in link"}
              </button>
            </form>
            <GoogleContinueButton onClick={handleGoogle} loading={googleBusy} disabled={busy} />
          </>
        )}

        {error ? (
          <p className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</p>
        ) : null}

        {sent ? (
          <button
            type="button"
            onClick={() => {
              setSent(false);
              setError("");
            }}
            className="mt-4 w-full text-center text-sm font-medium text-blue-700"
          >
            Use a different email
          </button>
        ) : (
          <button
            type="button"
            onClick={onFullSignup}
            className="mt-4 w-full text-center text-sm font-medium text-blue-700"
          >
            Prefer email and password? Create account
          </button>
        )}
        <button type="button" onClick={onClose} className="mt-2 w-full text-center text-sm text-slate-500">
          {closeLabel}
        </button>
      </div>
    </div>
  );
}
